import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  getFeedbackQuestionAction,
  setUpdateFeedbackQuestionAction,
  updateFeedbackQuestionAction,
} from "../../../store/reducers/customerFeedbackSlice";
import Notification from "../../../components/common/notification/Notification";
import { getUserProfile, setUserAction } from "../../../store/reducers/userSlice";

const CustomerFeedback = () => {
  const dispatch = useDispatch();
  const { feedbackQuestions, isUpdateFeedbackQuestionResponse } = useSelector(
    (state: any) => state.customerFeedback
  );
  const { user,userProfile } = useSelector((state: any) => state?.user);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);

  const [formData, setFormData] = useState<any>({});

  // Initialize formData when feedbackQuestions change
  useEffect(() => {
    if (feedbackQuestions?.questions?.length > 0) {
      let initialFormData: any = {};
      feedbackQuestions.questions.forEach((question: any) => {
        initialFormData[question.question_id] = {
          rating: user?.user?.is_asked_for_feedback
            ? null
            : question.rating ?? null, // Initialize with rating if exists
          comment: user?.user?.is_asked_for_feedback
            ? ""
            : question.comment ?? "", // Initialize with comment if exists
        };
      });
      setFormData(initialFormData);
    }
  }, [feedbackQuestions]);

  useEffect(() => {
    dispatch(getFeedbackQuestionAction());
    if (isUpdateFeedbackQuestionResponse) {
      dispatch(setUpdateFeedbackQuestionAction(false));
      // Update local storage for user after dispatching
      const updatedUser = {
        ...user,
        user: { ...user.user, is_asked_for_feedback: false,is_feedback_submitted: true },
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      // Option 1: Update Redux state
      dispatch(setUserAction(updatedUser));
      dispatch(getUserProfile());
    }
  }, [dispatch, isUpdateFeedbackQuestionResponse]);

  // Function to handle change for both radio and text field
  const onChangeAction = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: number,
    type: "rating" | "comment"
  ) => {
    const value =
      type === "rating" ? Number(event.target.value) : event.target.value;

    setFormData((prevFormData: any) => ({
      ...prevFormData,
      [id]: {
        ...prevFormData[id],
        [type]: value,
      },
    }));
  };

  // Check if any non-suggestion question is filled
  useEffect(() => {
    const isAnyNonSuggestionFilled = feedbackQuestions?.questions
      ?.filter((question: any) => question.category !== "suggestion") // Exclude suggestion questions
      ?.every((question: any) => formData[question.question_id]?.rating); // Check if any non-suggestion question has a rating

    setIsSubmitDisabled(!isAnyNonSuggestionFilled); // Enable the submit button when any rating is filled
  }, [formData, feedbackQuestions]);

  useEffect(() => {
    console.log("formData", formData);
  }, [formData]);

  // Submit the form data
  const handleSubmit = () => {
    const dynamicPayload = feedbackQuestions?.questions.map(
      (question: any) => ({
        question_id: question.question_id,
        comment: formData[question.question_id]?.comment || "",
        rating: formData[question.question_id]?.rating || null,
      })
    );
    dispatch(updateFeedbackQuestionAction(dynamicPayload));
  };

  return (
    <>
      <Notification />
      {feedbackQuestions?.thankYouMessage &&
        feedbackQuestions?.is_feedback_submitted &&
        !userProfile.is_asked_for_feedback && (
          <Alert severity="success">{feedbackQuestions?.thankYouMessage}</Alert>
        )}
      <Box
        style={{
          padding: "0px 32px",
          border: "2px solid #f4f4f4",
          borderRadius: "10px",
          background: "#fff",
        }}
      >
        <Grid container sx={{ marginTop: 2, paddingTop: 2 }}>
          <Grid md={7}></Grid>
          <Grid md={5} sx={{ display: "flex", justifyContent: "space-around" }}>
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", textAlign: "center" }}
            >
              Highly <br />
              Unsatisfied
            </Typography>
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", textAlign: "center" }}
            >
              Unsatisfied
            </Typography>
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", textAlign: "center" }}
            >
              Neither Satisfied <br /> nor Unsatisfied
            </Typography>
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", textAlign: "center" }}
            >
              Satisfied
            </Typography>
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", textAlign: "center" }}
            >
              High Satisfied
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            borderTop: "2px solid #F0EFF0",
            paddingTop: 3,
            marginTop: 2,
            paddingBottom: 2,
          }}
        >
          {feedbackQuestions?.questions?.length > 0 &&
            feedbackQuestions.questions.map((item: any, i: number) => (
              <Grid key={i} item md={12} sx={{ display: "flex" }}>
                <Grid md={7}>
                  <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
                    {item.question_text}
                  </Typography>
                </Grid>
                <Grid
                  md={5}
                  sx={{ display: "flex", justifyContent: "space-around" }}
                >
                  {item.category !== "suggestion" ? (
                    <RadioGroup
                      row
                      value={formData[item.question_id]?.rating || ""}
                      onChange={(e) =>
                        onChangeAction(e, item.question_id, "rating")
                      }
                      sx={{ display: "contents" }}
                    >
                      {[1, 2, 3, 4, 5].map((status) => (
                        <FormControlLabel
                          disabled={
                            feedbackQuestions?.is_feedback_submitted &&
                            !userProfile?.is_asked_for_feedback
                          }
                          key={status}
                          value={status.toString()}
                          sx={{
                            fontSize: "12px",
                            marginBottom: "4px",
                            ".MuiTypography-root": {
                              fontSize: "12px",
                            },
                          }}
                          control={
                            <Radio
                              size="small"
                              sx={{
                                transform: "scale(0.8)",
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                          }
                          label={""}
                        />
                      ))}
                    </RadioGroup>
                  ) : (
                    <TextField
                      fullWidth
                      disabled={
                        feedbackQuestions?.is_feedback_submitted &&
                        !userProfile?.is_asked_for_feedback
                      }
                      name="comments"
                      value={formData[item.question_id]?.comment || ""}
                      onChange={(e) =>
                        onChangeAction(e, item.question_id, "comment")
                      }
                      variant="outlined"
                      sx={{ marginTop: "0px !important" }}
                      multiline
                      rows={3}
                      InputProps={{
                        sx: {
                          padding: "8px",
                        },
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            ))}

          <Grid
            item
            md={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "end",
            }}
          >
            {/* Submit Button */}
            <Button
              disabled={isSubmitDisabled}
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                marginTop: 4,
                width: "160px",
                height: "40px",
                display:
                  feedbackQuestions?.is_feedback_submitted &&
                  !userProfile?.is_asked_for_feedback
                    ? "none"
                    : "block",
              }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CustomerFeedback;
